import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { weathered } from '../materials/materials';

// The Blender-built models (art/blender -> public/models). They carry
// geometry, UVs in metres and material names; the game supplies its own
// shaders (paint zones, battle damage, wetness, fire) by those names. If a
// file is missing or fails to load, the procedural builders stand in.

/** Blender material names that differ from the game's material keys. */
const ALIAS: Record<string, string> = { ropeT: 'ropeTarred' };

/** Nodes the game animates or swaps itself; everything else in a ship file is static. */
export const DYNAMIC_NODES = new Set(['rudder', 'wheel', 'hull_lod']);

export class ArtSet {
  constructor(readonly root: THREE.Object3D) {}

  node(name: string): THREE.Object3D | undefined {
    return this.root.getObjectByName(name) ?? undefined;
  }

  /** Top-level nodes (the objects exported from Blender). */
  get nodes(): THREE.Object3D[] {
    return this.root.children;
  }
}

export class Art {
  readonly ships = new Map<string, ArtSet>();
  rig: ArtSet | null = null;
  props: ArtSet | null = null;
  crew: ArtSet | null = null;

  static async load(base = 'models/', classes: readonly string[] = [], onProgress?: (label: string) => void): Promise<Art> {
    const art = new Art();
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    // Only ask for the models the art pipeline has actually exported.
    let available: Set<string>;
    try {
      const res = await fetch(base + 'manifest.json');
      available = new Set(((await res.json()) as { models: string[] }).models);
    } catch {
      return art;
    }
    const get = async (file: string): Promise<ArtSet | null> => {
      if (!available.has(file)) return null;
      try {
        onProgress?.(file);
        const gltf = await loader.loadAsync(base + file);
        gltf.scene.updateMatrixWorld(true);
        gltf.scene.traverse((o) => {
          // Blender object names are unique per .blend, so the art pipeline
          // prefixes them ('raider__rudder'); the game uses the bare names.
          o.name = o.name.replace(/^.*__/, '');
          // Blender exports custom attributes with a leading underscore.
          const g = (o as THREE.Mesh).geometry;
          const a = (o as THREE.Mesh).isMesh ? g.getAttribute('_grime') : undefined;
          if (a) {
            g.setAttribute('grime', a);
            g.deleteAttribute('_grime');
          }
        });
        return new ArtSet(gltf.scene);
      } catch {
        return null;
      }
    };
    const [rig, props, crew, ...ships] = await Promise.all([get('rig.glb'), get('props.glb'), get('crew.glb'), ...classes.map((c) => get(`ship_${c}.glb`))]);
    art.rig = rig;
    art.props = props;
    art.crew = crew;
    classes.forEach((c, i) => {
      const s = ships[i];
      if (s) art.ships.set(c, s);
    });
    return art;
  }
}

function materialKey(m: THREE.Material | THREE.Material[]): string {
  const name = Array.isArray(m) ? m[0]?.name ?? '' : m.name;
  return ALIAS[name] ?? name;
}

/**
 * A copy of a model node sharing its geometry, with the game's materials.
 * `local`: drop the node's own placement (the caller positions it).
 */
export function instantiate(node: THREE.Object3D, materials: Record<string, THREE.Material>, local = false): THREE.Object3D {
  const c = node.clone(true);
  if (local) {
    c.position.set(0, 0, 0);
    c.quaternion.identity();
    c.scale.set(1, 1, 1);
  }
  c.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const key = materialKey(m.material);
    m.material = gameMaterial(materials, key, m.geometry);
    // Named by material, as the procedural batches are (the distance LOD restyles by name).
    m.name = key;
    m.castShadow = true;
    m.receiveShadow = true;
  });
  return c;
}

/** The game's material for a model mesh (weathered when it carries the bake). */
export function gameMaterial(materials: Record<string, THREE.Material>, key: string, geometry: THREE.BufferGeometry): THREE.Material {
  const base = materials[key] ?? materials.timber!;
  return geometry.getAttribute('grime') ? weathered(base, key) : base;
}

/** The mesh primitives of a node (one per material), with their keys. */
export function primitives(node: THREE.Object3D): { key: string; geometry: THREE.BufferGeometry }[] {
  const out: { key: string; geometry: THREE.BufferGeometry }[] = [];
  node.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) out.push({ key: materialKey(m.material), geometry: m.geometry });
  });
  return out;
}
